import {
  useState,
  MouseEvent,
  KeyboardEvent,
  useEffect,
  forwardRef,
  useCallback,
  createRef,
} from 'react';

import { Popover } from 'react-tiny-popover';

import {
  DeleteIcon,
  PlusIcon,
  PersonIcon,
  DownArrowIcon,
  UpArrowIcon,
} from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { validateGuestsChildrenAges } from '@components/views/landing/components/booker/common/bookerUtils';
import { validRoomChildAge } from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveListingSearchGuest';
import { Icon } from '@components/Icon';
import useIsMounted from '@hooks/useIsMounted';
import { generateUUID } from '@utils/generateUUID';

import ListingSearchRoomItem from './SearchRoomItem';
import {
  KidInterface,
  ListingSearchGuestProps,
} from './ListingSearchGuestInterface';
import {
  AddNewRoomBox,
  AddNewRoomButton,
  AddNewRoomButtonText,
  GuestContent,
  GuestTabs,
  SearchGuestItem,
  SearchGuestItemLabel,
  SearchGuestItemText,
  SearchGuestItemTextWrapper,
  SearchGuestIconWrapper,
  RoomButton,
  Wrapper,
} from './SearchGuestStyle';
import { CustomPopoverChildComponentProps } from '../SearchBarInput/SearchBarInput';

const CustomPopoverChildComponent = forwardRef<
  HTMLDivElement,
  CustomPopoverChildComponentProps
>((props, ref) => (
  <SearchGuestItem ref={ref} onClick={props.onClick}>
    {props.children}
  </SearchGuestItem>
));

const ListingSearchGuest = ({
  rooms,
  setHotelRooms,
  popoverStyles,
  isBookerSearch = false,
  isHotelListing = false,
  searchText = 'Kaç Kişi Konaklayacaksınız?',
  disableAddRoomButton,
  maxAdultCount = 6,
}: ListingSearchGuestProps) => {
  const [roomId, setRoomId] = useState<string>();
  const [activeKey, setActiveTabKey] = useState<string | undefined>(
    rooms[0].key
  );
  const [selectedGuest, setSelectedGuest] = useState(false);
  const toggleSelectGuest = () => setSelectedGuest((val) => !val);

  // ===================================
  // Handle toggle the select box
  // ===================================
  const handleSelect = (): void => {
    const isError = validateGuestsChildrenAges(rooms);
    if (isError) {
      handleChildError();
      focusRoomWithError();
      return;
    }
    setTimeout(() => toggleSelectGuest());
  };

  // ===================================
  // Focus the room that has an error
  // ===================================
  const focusRoomWithError = () => {
    const roomWithError = rooms.find((room) =>
      room.childrenList.find(
        (kid: KidInterface) => !validRoomChildAge(String(kid.age))
      )
    );

    if (!roomWithError) return;

    setActiveTabKey(roomWithError.key);
  };

  // ===================================
  // Format the values before display them
  // ===================================
  const adultNumber = rooms.reduce((t, a) => t + a.adultCount, 0);
  const childrenCount = rooms.reduce((t, a) => t + a.childrenList.length, 0);
  const roomSelectBoxLabel = `${rooms.length} Oda, ${adultNumber} Yetişkin   ${
    childrenCount > 0 ? ', ' + childrenCount + ' Çocuk' : ''
  }`;

  // ===================================
  // Handle the change on the rooms tabs
  // ===================================
  const handleChange = (ak: string): void => {
    setActiveTabKey(ak);
  };

  // ===================================
  // Handle edit/remove rooms tabs
  // ===================================
  const handleEdit = useCallback(
    (
      id:
        | string
        | MouseEvent<Element, globalThis.MouseEvent>
        | KeyboardEvent<Element>,
      info: any
    ): void => {
      const newRooms = rooms.filter((room) => room.key !== info.key);
      setHotelRooms?.(newRooms);

      if (info.key === rooms[0].key) {
        setActiveTabKey(rooms[1].key);
      } else {
        setActiveTabKey(rooms[0].key);
      }
    },
    [rooms]
  );

  // ==========================================================================
  // -Custom functions to control number of adult in each room
  // -using this function we can increase the number of adult guest in each room
  // ==========================================================================
  const handleIncrementAdultNumber = (id: string): void => {
    setHotelRooms((currentRooms) =>
      currentRooms?.map((room) =>
        room.id === id && room.adultCount < maxAdultCount
          ? {
              ...room,
              adultCount: room.adultCount + 1,
            }
          : room
      )
    );
  };

  // ==========================================================================
  // -Custom functions to control number of adult in each room
  // -using this function we can decrease the number of adult guest in each room
  // ==========================================================================
  const handleDecrementAdultNumber = (id: string): void => {
    setHotelRooms((currentRooms) =>
      currentRooms?.map((room) =>
        room.id === id ? { ...room, adultCount: room.adultCount - 1 } : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control number of kids in each room
  // -using this function we can add new kid in each room
  // =============================================================
  const addNewChild = (roomId: string, newKid: KidInterface): void => {
    setHotelRooms((hotelRooms) =>
      hotelRooms?.map((room) =>
        room.id === roomId && room.childrenList?.length < 3
          ? { ...room, childrenList: [...room.childrenList, newKid] }
          : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control number of kids in each room
  // -using this function we can remove a kid from each room
  // =============================================================
  const removeChild = (roomId: string, kidId: string): void => {
    setHotelRooms((currentRooms) =>
      currentRooms?.map((room) =>
        room.id === roomId
          ? {
              ...room,
              childrenList: [
                ...room.childrenList.slice(0, Number(kidId)),
                ...room.childrenList.slice(Number(kidId) + 1),
              ],
            }
          : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control the age of kids in each room
  // -using this function we can change the kid's age
  // =============================================================
  const changeChildAge = (kidId: string, age: string): void => {
    setHotelRooms((hr) =>
      hr?.map((room) =>
        room.childrenList.length !== 0
          ? {
              ...room,
              childrenList:
                room?.childrenList &&
                room?.childrenList?.map((kid) =>
                  kid.id === kidId
                    ? {
                        ...kid,
                        age: age,
                        error: validRoomChildAge(age) ? false : true,
                      }
                    : kid
                ),
            }
          : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control the rooms
  // -using this function we can add new room
  // =============================================================
  const addRoom = (): void => {
    setHotelRooms([
      ...rooms,
      {
        id: generateUUID(),
        adultCount: 2,
        childrenList: [],
        title: `${rooms.length + 1}. Oda`,
        closable: true,
        error: false,
        key: String(rooms[rooms.length - 1].key + 1),
        ref: createRef<HTMLDivElement>(),
      },
    ]);
  };

  const handleChildIncrement = (roomId: string) => {
    const newChild = {
      id: Date.now().toString(),
      age: '',
      error: false,
      inputId: `child-input-${generateUUID()}`,
      ref: createRef<HTMLDivElement>(),
    };

    addNewChild(roomId, newChild);
    setRoomId(roomId);
  };

  // ============================================================
  // -Custom functions to control the children error
  // -using this function we can find an invalid child age and update te error property
  // =============================================================
  const handleChildError = (): void => {
    setHotelRooms((hr) =>
      hr?.map((room) =>
        room.childrenList.length !== 0
          ? {
              ...room,
              childrenList:
                room?.childrenList &&
                room?.childrenList?.map((kid: KidInterface) =>
                  validRoomChildAge(String(kid.age))
                    ? { ...kid, error: false }
                    : { ...kid, error: true }
                ),
            }
          : room
      )
    );
  };

  useEffect(() => {
    const room = rooms.find((item) => item.id === roomId);

    if (room?.ref) {
      room.ref.current?.scrollTo({
        top: room.childrenList.length === 3 ? 220 : 130,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [rooms, roomId]);

  useEffect(() => {
    if (rooms.length === 1) {
      setActiveTabKey(rooms[0].key);
    }
  }, [rooms, handleEdit]);

  const [parentRef, parentElement] = useIsMounted<HTMLDivElement>();

  return (
    <Wrapper ref={parentRef} isHotelListing={Boolean(isHotelListing)}>
      {parentElement ? (
        <Popover
          positions={['bottom', 'left']}
          align='start'
          padding={10}
          containerStyle={{
            position: isBookerSearch ? 'relative' : 'fixed',
            zIndex: '100',
            backgroundColor: 'white',
            width: '580px',
            display: 'flex',
            justifyContent: 'center',
            margin: '0px',
            boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
            ...popoverStyles,
          }}
          isOpen={selectedGuest}
          parentElement={parentElement}
          onClickOutside={() => {
            const isError = validateGuestsChildrenAges(rooms);
            if (isError) {
              handleChildError();
              focusRoomWithError();
              return;
            }
            setSelectedGuest(false);
          }}
          content={() => {
            return (
              <GuestContent>
                <GuestTabs
                  activeKey={activeKey}
                  defaultActiveKey='1'
                  editable={{
                    onEdit: (type: 'remove', info) => handleEdit(type, info),
                  }}
                  onChange={handleChange}
                >
                  {rooms.length > 0 &&
                    rooms?.map((room, index) => {
                      return (
                        <GuestTabs.TabPane
                          closable={rooms.length === 1 ? false : room.closable}
                          tab={`${index + 1}. Oda`}
                          key={room.key}
                          closeIcon={
                            <Icon
                              icon={DeleteIcon}
                              className='guest-search-delete-icon'
                              width={12}
                              height={12}
                            />
                          }
                        >
                          <ListingSearchRoomItem
                            adultGuestCount={room.adultCount}
                            childGuestCount={room.childrenList.length}
                            childrenList={room.childrenList}
                            guestsWrapperRef={room.ref}
                            handleAdultNumberDecrement={() =>
                              handleDecrementAdultNumber(room.id)
                            }
                            handleAdultNumberIncrement={() =>
                              handleIncrementAdultNumber(room.id)
                            }
                            handleChildNumberDecrement={() =>
                              removeChild(
                                room.id,
                                (room.childrenList.length - 1).toString()
                              )
                            }
                            handleChildNumberIncrement={() =>
                              handleChildIncrement(room.id)
                            }
                            changeChildAge={changeChildAge}
                          />
                        </GuestTabs.TabPane>
                      );
                    })}
                </GuestTabs>

                {disableAddRoomButton ? null : (
                  <AddNewRoomBox>
                    {!validateGuestsChildrenAges(rooms) ? (
                      <AddNewRoomButton
                        disabled={rooms.length === 6 ? true : false}
                        onClick={addRoom}
                        outline
                        data-testid='listing-search-new-room'
                      >
                        <AddNewRoomButtonText>Oda Ekle</AddNewRoomButtonText>
                        <CustomIcon
                          icon={PlusIcon}
                          fontSize={20}
                          height={20}
                          paletteColor='coral'
                        />
                      </AddNewRoomButton>
                    ) : (
                      <RoomButton
                        onClick={() => {
                          handleChildError();
                          focusRoomWithError();
                        }}
                      >
                        <AddNewRoomButtonText>Oda Ekle</AddNewRoomButtonText>
                        <CustomIcon
                          icon={PlusIcon}
                          fontSize={20}
                          height={20}
                          paletteColor='coral'
                        />
                      </RoomButton>
                    )}
                  </AddNewRoomBox>
                )}
              </GuestContent>
            );
          }}
        >
          <CustomPopoverChildComponent onClick={handleSelect}>
            <CustomIcon icon={PersonIcon} fontSize={28} paletteColor='coral' />
            <SearchGuestItemTextWrapper>
              <SearchGuestItemLabel>{searchText}</SearchGuestItemLabel>
              <SearchGuestItemText>{roomSelectBoxLabel}</SearchGuestItemText>
            </SearchGuestItemTextWrapper>
            {selectedGuest ? (
              <SearchGuestIconWrapper>
                <CustomIcon
                  icon={UpArrowIcon}
                  fontSize={16}
                  paletteColor='darkGray'
                />
              </SearchGuestIconWrapper>
            ) : (
              <SearchGuestIconWrapper>
                <CustomIcon
                  icon={DownArrowIcon}
                  fontSize={16}
                  paletteColor='darkGray'
                />
              </SearchGuestIconWrapper>
            )}
          </CustomPopoverChildComponent>
        </Popover>
      ) : null}
    </Wrapper>
  );
};

export default ListingSearchGuest;
